/**
 * Late documentation label helpers.
 * Clinical metadata only — never blocks save or warns the nurse.
 */

export function normalizeCareTime(value) {
  if (value == null || value === '') return null;
  const match = String(value).trim().match(/(\d{1,2}):(\d{2})/);
  if (!match) return null;
  const hours = Number(match[1]);
  const minutes = Number(match[2]);
  if (Number.isNaN(hours) || Number.isNaN(minutes) || hours > 23 || minutes > 59) {
    return null;
  }
  return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
}

/** Current local clock as HH:MM (24h). */
export function getCurrentCareTime(now = new Date()) {
  const date = now instanceof Date ? now : new Date(now);
  if (Number.isNaN(date.getTime())) return '00:00';
  return `${String(date.getHours()).padStart(2, '0')}:${String(date.getMinutes()).padStart(2, '0')}`;
}

/** Prefer explicit care_time; else earliest medication/fluid clock time. */
export function deriveCareTime(note) {
  if (!note) return null;
  const direct = normalizeCareTime(note.care_time);
  if (direct) return direct;

  const meds = note.medications_administered?.length
    ? note.medications_administered
    : note.medications || [];
  const medTimes = meds
    .map((med) => normalizeCareTime(med?.time_given))
    .filter(Boolean)
    .sort();
  if (medTimes.length) return medTimes[0];

  const fluidTimes = [...(note.fluid_inputs || []), ...(note.fluid_outputs || [])]
    .map((row) => normalizeCareTime(row?.time_given || row?.time))
    .filter(Boolean)
    .sort();
  if (fluidTimes.length) return fluidTimes[0];

  return null;
}

/**
 * Resolve absolute care datetime.
 * careDayOffset: 0 = same calendar day as documentation, 1 = yesterday.
 * null = auto (if care clock is after documentation, treat as yesterday).
 */
export function resolveCareDateTime(careTime, documentationTime, careDayOffset = null) {
  const normalized = normalizeCareTime(careTime);
  if (!normalized) return null;

  const [hours, minutes] = normalized.split(':').map(Number);
  const documentedAt = new Date(documentationTime);
  if (Number.isNaN(documentedAt.getTime())) return null;

  const careDate = new Date(documentedAt);
  careDate.setHours(hours, minutes, 0, 0);

  const offset = careDayOffset == null ? null : Number(careDayOffset);
  if (offset === 1) {
    careDate.setDate(careDate.getDate() - 1);
  } else if (offset === 0) {
    // Keep same calendar day as documentation.
  } else if (careDate > documentedAt) {
    careDate.setDate(careDate.getDate() - 1);
  }

  return careDate;
}

/**
 * Compute late-entry audit fields from care vs documentation times.
 */
export function getLateEntryDetails(careTime, documentationTime, careDayOffset = null) {
  const documentedAt = new Date(documentationTime);
  if (Number.isNaN(documentedAt.getTime())) {
    return { isLateEntry: false, diffMinutes: null, careDate: null };
  }

  const careDate = resolveCareDateTime(careTime, documentedAt, careDayOffset);
  if (!careDate) {
    return { isLateEntry: false, diffMinutes: null, careDate: null };
  }

  const diffMinutes = Math.round((documentedAt - careDate) / 1000 / 60);
  return {
    isLateEntry: diffMinutes > 60,
    diffMinutes: Math.max(0, diffMinutes),
    careDate,
  };
}

export function getLateEntryStatus(careTime, documentationTime, careDayOffset = null) {
  return getLateEntryDetails(careTime, documentationTime, careDayOffset).isLateEntry;
}

/** Effective Today(0)/Yesterday(1) for UI when offset is auto (null). */
export function getEffectiveCareDayOffset(careTime, documentationTime, careDayOffset = null) {
  if (careDayOffset === 0 || careDayOffset === 1) return careDayOffset;
  const careDate = resolveCareDateTime(careTime, documentationTime, null);
  const documentedAt = new Date(documentationTime);
  if (!careDate || Number.isNaN(documentedAt.getTime())) return 0;
  const sameDay =
    careDate.getFullYear() === documentedAt.getFullYear()
    && careDate.getMonth() === documentedAt.getMonth()
    && careDate.getDate() === documentedAt.getDate();
  return sameDay ? 0 : 1;
}

/**
 * Night-shift default for the Today/Yesterday picker toggle.
 * Overnight (00:00–06:59) → Yesterday; otherwise Today.
 */
export function getDefaultCareDayOffset(now = new Date()) {
  const date = now instanceof Date ? now : new Date(now);
  if (Number.isNaN(date.getTime())) return 0;
  const currentHour = date.getHours();
  return currentHour >= 0 && currentHour < 7 ? 1 : 0;
}

/** Closed-field label: "08:00" or "Yesterday 23:00". */
export function formatCareTimeLabel(careTime, careDayOffset = 0, language = 'en') {
  const time = normalizeCareTime(careTime);
  if (!time) return language === 'ar' ? 'غير محدد' : 'Not specified';
  if (careDayOffset === 1) {
    return language === 'ar' ? `أمس ${time}` : `Yesterday ${time}`;
  }
  return time;
}

/**
 * PDF label: time only for today; "16 Jul 23:00" when care was yesterday.
 */
export function formatCareTimeForPdf(careTime, documentationTime, careDayOffset = null) {
  const time = normalizeCareTime(careTime);
  if (!time) return 'Not specified';

  const offset = getEffectiveCareDayOffset(careTime, documentationTime, careDayOffset);
  if (offset !== 1) return time;

  const careDate = resolveCareDateTime(careTime, documentationTime, 1);
  if (!careDate) return `Yesterday ${time}`;

  const day = careDate.getDate();
  const month = careDate.toLocaleString('en-GB', { month: 'short' });
  return `${day} ${month} ${time}`;
}

/** HH:MM from the UTC portion of an ISO string (models often copy this by mistake). */
function utcClockFromIso(iso) {
  const match = String(iso || '').match(/T(\d{2}):(\d{2})/);
  return match ? `${match[1]}:${match[2]}` : null;
}

/**
 * Attach late-entry audit fields.
 * If no care time can be derived, default to the device local clock (not late).
 * Never default from model datetime — ISO datetimes are UTC and look like the wrong local time.
 */
export function applyLateEntryMetadata(note, documentationTime = new Date()) {
  if (!note) return note;
  const documentationIso =
    documentationTime instanceof Date
      ? documentationTime.toISOString()
      : String(documentationTime || new Date().toISOString());

  const direct = normalizeCareTime(note.care_time);
  const fromEvents = deriveCareTime({ ...note, care_time: null });
  let care_time = direct || fromEvents;

  // Models often set care_time to the UTC HH:MM of datetime when nothing was spoken.
  const utcClock =
    utcClockFromIso(documentationIso) || utcClockFromIso(note.datetime);
  if (direct && !fromEvents && utcClock && direct === utcClock) {
    care_time = null;
  }

  care_time = care_time || getCurrentCareTime();
  const care_day_offset =
    note.care_day_offset === 0 || note.care_day_offset === 1
      ? note.care_day_offset
      : null;

  const details = getLateEntryDetails(care_time, documentationIso, care_day_offset);

  return {
    ...note,
    care_time,
    care_day_offset,
    documentation_time: documentationIso,
    is_late_entry: details.isLateEntry,
    late_entry_diff_minutes: details.diffMinutes,
  };
}

/** Resolve display/audit timing from a saved session note or nested content. */
export function resolveNoteTiming(note) {
  const content = note?.openaiNote || note?.note_content || note || {};
  const documentationTime =
    note?.documentation_time
    || content.documentation_time
    || note?.datetime
    || content.datetime
    || new Date().toISOString();

  const careTime =
    normalizeCareTime(note?.care_time)
    || normalizeCareTime(content.care_time)
    || deriveCareTime(content)
    || null;

  const careDayOffset =
    note?.care_day_offset === 0 || note?.care_day_offset === 1
      ? note.care_day_offset
      : content.care_day_offset === 0 || content.care_day_offset === 1
        ? content.care_day_offset
        : null;

  const details = getLateEntryDetails(careTime, documentationTime, careDayOffset);

  const isLateEntry =
    typeof note?.is_late_entry === 'boolean'
      ? note.is_late_entry
      : typeof content.is_late_entry === 'boolean'
        ? content.is_late_entry
        : details.isLateEntry;

  const diffMinutes =
    typeof note?.late_entry_diff_minutes === 'number'
      ? note.late_entry_diff_minutes
      : typeof content.late_entry_diff_minutes === 'number'
        ? content.late_entry_diff_minutes
        : details.diffMinutes;

  return {
    careTime,
    careDayOffset,
    documentationTime,
    isLateEntry,
    diffMinutes,
  };
}
